import { useMemo } from "react";
import type { LlmEntry, MagiMethod } from "../config";
import type { ModelError, ModelResponse } from "../types";
import { LlmPill } from "./LlmPill";
import { friendlyLabel } from "../utils/llmLabels";

export function ResponseCards({
  method,
  responses,
  errors,
  availableLlms,
  onSelect,
  selected,
}: {
  method: MagiMethod;
  responses: ModelResponse[];
  errors?: ModelError[];
  availableLlms: LlmEntry[];
  onSelect: (model: string) => void;
  selected: string | null;
}) {
  const meta = useMemo(
    () => new Map(availableLlms.map((l) => [l.id, l])),
    [availableLlms],
  );
  if (responses.length === 0 && (errors?.length ?? 0) === 0) return null;
  return (
    <section aria-label="Individual model responses">
      <h3 className="section-label">Individual responses</h3>
      <div className="response-list">
        {responses.map((r) => {
          const { label, color } = voteFor(method, r);
          const confPct =
            typeof r.confidence_score === "number" && r.confidence_score >= 0
              ? Math.round(r.confidence_score * 100)
              : null;
          return (
            <button
              key={r.model}
              className="response-card"
              onClick={() => onSelect(r.model)}
              aria-pressed={selected === r.model}
              data-testid={`response-card-${r.model}`}
              style={{
                borderColor: selected === r.model ? "var(--accent)" : undefined,
              }}
            >
              <ConfidenceBadge pct={confPct} color={color} method={method} />
              <div className="main">
                <div className="name">
                  {voteDot(method, r)}
                  <LlmPill id={r.model} label={friendlyLabel(r.model, meta)} />
                </div>
                <div className="reason">
                  {summariseReason(r)}
                </div>
              </div>
              <div className="vote" style={{ color }}>
                {label}
              </div>
            </button>
          );
        })}
        {(errors ?? []).map((e) => (
          <div
            key={e.model}
            className="response-card"
            data-testid={`response-card-error-${e.model}`}
            style={{
              borderColor: "var(--negative)",
              cursor: "default",
              color: "var(--text)",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "1px solid var(--negative)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--negative)",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              ×
            </div>
            <div className="main">
              <div className="name">
                <LlmPill id={e.model} label={friendlyLabel(e.model, meta)} />
              </div>
              <div className="reason" title={e.error}>
                {e.error_category ? `${e.error_category}: ` : ""}
                {e.error}
              </div>
            </div>
            <div className="vote" style={{ color: "var(--negative)" }}>
              FAILED
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Replaces the old mini-ring with a dual-element badge: a visual arc PLUS a
 * labelled percentage. Probability responses get "%" label (the score IS the
 * estimate there); everything else shows "CONF" to make the provenance of
 * the number unambiguous.
 */
function ConfidenceBadge({
  pct,
  color,
  method,
}: {
  pct: number | null;
  color: string;
  method: MagiMethod;
}) {
  if (pct === null) {
    return (
      <div
        title="No confidence reported (model abstained)"
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          border: "1px dashed var(--border-strong)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-dim)",
          fontSize: 12,
        }}
      >
        —
      </div>
    );
  }
  const r = 20;
  const c = 2 * Math.PI * r;
  const v = pct / 100;
  const unitLabel = method === "Probability" ? "PROB" : "CONF";
  return (
    <div
      style={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}
      title={`${pct}% ${method === "Probability" ? "probability" : "confidence"}`}
    >
      <svg width="52" height="52" viewBox="-26 -26 52 52" aria-hidden>
        <circle r={r} className="ring-arc" stroke="var(--border)" />
        <circle
          r={r}
          className="ring-arc"
          stroke={color}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - v)}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
          pointerEvents: "none",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700 }}>{pct}%</span>
        <span
          style={{
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: "0.1em",
            color: "var(--text-dim)",
            marginTop: 2,
          }}
        >
          {unitLabel}
        </span>
      </div>
    </div>
  );
}

function summariseReason(r: ModelResponse): string {
  // The reason line is a single-row ellipsised teaser. Collapse newlines so
  // only the first paragraph shows, and stringify objects so we never get
  // the dreaded `[object Object]`.
  const base =
    r.reason ||
    r.error ||
    (typeof r.response === "object" && r.response !== null
      ? JSON.stringify(r.response)
      : String(r.response ?? ""));
  return base.replace(/\s*\n+\s*/g, " · ");
}

function voteDot(method: MagiMethod, r: ModelResponse): JSX.Element | null {
  if (r.error) return null;
  if (method === "VoteYesNo") {
    const v = String(r.response).toLowerCase();
    const dot = v === "yes" ? "🟢" : v === "no" ? "🔴" : "🟡";
    return <span aria-hidden style={{ marginRight: 6, fontSize: 10 }}>{dot}</span>;
  }
  if (method === "Probability") {
    return <span aria-hidden style={{ marginRight: 6, fontSize: 10 }}>🔵</span>;
  }
  return null;
}

function voteFor(method: MagiMethod, r: ModelResponse): { label: string; color: string } {
  if (r.error) return { label: "ERROR", color: "var(--negative)" };
  if (method === "VoteYesNo") {
    const v = String(r.response).toLowerCase();
    if (v === "yes") return { label: "YES", color: "var(--positive)" };
    if (v === "no") return { label: "NO", color: "var(--negative)" };
    return { label: "ABSTAIN", color: "var(--accent)" };
  }
  if (method === "Probability") {
    // The confidence_score IS the probability.
    const n = typeof r.confidence_score === "number" ? r.confidence_score : NaN;
    if (!Number.isFinite(n) || n < 0) return { label: "ABSTAIN", color: "var(--accent)" };
    return {
      label: `${Math.round(n * 100)}%`,
      color: n >= 0.5 ? "var(--positive)" : "var(--negative)",
    };
  }
  if (method === "VoteOptions") {
    return { label: String(r.response ?? "").toUpperCase().slice(0, 14), color: "var(--accent)" };
  }
  return { label: "VIEW", color: "var(--accent)" };
}
